import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { platformAPI } from '../../lib/platformApi';
import {
  Wallet, CreditCard, AlertCircle, CheckCircle2, Plus,
  Zap, Search, RefreshCw, Copy, User, Loader2
} from 'lucide-react';

export default function PlatformWalletActivation() {
  const [stats, setStats] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [activatingId, setActivatingId] = useState(null);
  const [bulkActivating, setBulkActivating] = useState(false);

  const [newUser, setNewUser] = useState({
    user_id: '', name: '', email: '', phone: '', bvn: '', activate_va: true,
  });

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await platformAPI.getDashboardStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats', err);
    }
  }, []);

  const fetchWallets = useCallback(async () => {
    try {
      const params = { limit: 200, sort: 'newest' };
      if (filter === 'needs_va') params.has_va = false;
      if (filter === 'active') params.has_va = true;
      if (search) params.search = search;

      const { data } = await platformAPI.getWallets(params);
      setWallets(data.wallets || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to load wallets', err);
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchWallets(); }, [fetchWallets]);

  const createWallet = async (e) => {
    e.preventDefault();
    if (!newUser.user_id.trim() || !newUser.name.trim()) {
      toast.error('User ID and Name are required');
      return;
    }
    setCreating(true);
    try {
      const { data } = await platformAPI.createWalletFromDashboard(newUser);
      toast.success(`Wallet created for ${data.wallet.name}${data.wallet.virtual_account.has_va ? ' with VA ' + data.wallet.virtual_account.number : ''}`);
      setNewUser({ user_id: '', name: '', email: '', phone: '', bvn: '', activate_va: true });
      setShowAddForm(false);
      fetchWallets();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create wallet');
    } finally {
      setCreating(false);
    }
  };

  const activateVA = async (walletId) => {
    setActivatingId(walletId);
    try {
      const { data } = await platformAPI.activateVA(walletId);
      toast.success(`VA activated: ${data.virtual_account.number}`);
      fetchWallets();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Activation failed');
    } finally {
      setActivatingId(null);
    }
  };

  const bulkActivate = async () => {
    setBulkActivating(true);
    try {
      const { data } = await platformAPI.bulkActivateVA();
      toast.success(`Activated ${data.activated} virtual accounts${data.failed > 0 ? `, ${data.failed} failed` : ''}`);
      fetchWallets();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk activation failed');
    } finally {
      setBulkActivating(false);
    }
  };

  const copyVA = (number) => {
    navigator.clipboard.writeText(number);
    toast.success('Account number copied');
  };

  const needsVA = stats?.wallets?.without_va || 0;

  return (
    <div data-testid="platform-wallet-activation-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Wallet Activation
          </h1>
          <p className="text-gray-500 mt-1">Create wallets and activate virtual accounts for your users</p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          data-testid="add-user-btn"
          className="flex items-center gap-2 px-4 py-2.5 bg-[#E8678B] text-white rounded-lg text-sm font-semibold hover:bg-[#d4557a] transition-colors"
        >
          <Plus size={16} /> Add User
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="stat-total-wallets">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">Total Wallets</span>
            <Wallet size={18} className="text-gray-400" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats?.wallets?.total ?? '—'}</p>
        </div>
        <div className="bg-white rounded-xl border border-green-200 p-5" data-testid="stat-vas-active">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-green-700">VAs Active</span>
            <CheckCircle2 size={18} className="text-green-500" />
          </div>
          <p className="text-2xl font-bold text-green-700">{stats?.wallets?.with_va ?? '—'}</p>
        </div>
        <div className="bg-white rounded-xl border border-orange-200 p-5" data-testid="stat-need-activation">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-orange-700">Need Activation</span>
            <AlertCircle size={18} className="text-orange-500" />
          </div>
          <p className="text-2xl font-bold text-orange-700">{needsVA}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="stat-total-balance">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">Total Balance</span>
            <CreditCard size={18} className="text-gray-400" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            ₦{Number(stats?.volume?.total_balance_ngn || 0).toLocaleString()}
          </p>
        </div>
      </div>

      {/* Bulk Activate Banner */}
      {needsVA > 0 && (
        <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-xl flex items-center justify-between" data-testid="bulk-activate-banner">
          <div className="flex items-center gap-3">
            <Zap size={20} className="text-orange-600" />
            <div>
              <p className="text-sm font-semibold text-orange-800">
                {needsVA} wallet{needsVA > 1 ? 's' : ''} missing virtual accounts
              </p>
              <p className="text-xs text-orange-600">
                Users can't receive bank transfers until their VA is activated
              </p>
            </div>
          </div>
          <button
            onClick={bulkActivate}
            disabled={bulkActivating}
            data-testid="bulk-activate-btn"
            className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-semibold hover:bg-orange-700 transition-colors disabled:opacity-50 whitespace-nowrap"
          >
            {bulkActivating ? 'Activating...' : `Activate All (${needsVA})`}
          </button>
        </div>
      )}

      {/* Add New User Form */}
      {showAddForm && (
        <div className="mb-6 bg-white rounded-xl border border-gray-200 p-6" data-testid="add-user-form">
          <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <User size={18} /> Add New User
          </h3>
          <form onSubmit={createWallet} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User ID *</label>
                <input
                  type="text" required value={newUser.user_id}
                  onChange={e => setNewUser({ ...newUser, user_id: e.target.value })}
                  data-testid="input-user-id"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8678B]/20 focus:border-[#E8678B]"
                  placeholder="e.g. usr_001 or john@email.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                <input
                  type="text" required value={newUser.name}
                  onChange={e => setNewUser({ ...newUser, name: e.target.value })}
                  data-testid="input-full-name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8678B]/20 focus:border-[#E8678B]"
                  placeholder="Adebayo Ogunlesi"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email" value={newUser.email}
                  onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                  data-testid="input-email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8678B]/20 focus:border-[#E8678B]"
                  placeholder="user@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel" value={newUser.phone}
                  onChange={e => setNewUser({ ...newUser, phone: e.target.value })}
                  data-testid="input-phone"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8678B]/20 focus:border-[#E8678B]"
                  placeholder="+234..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">BVN (for higher limits)</label>
                <input
                  type="text" value={newUser.bvn} maxLength={11}
                  onChange={e => setNewUser({ ...newUser, bvn: e.target.value })}
                  data-testid="input-bvn"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8678B]/20 focus:border-[#E8678B]"
                  placeholder="11-digit BVN (optional)"
                />
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox" checked={newUser.activate_va}
                    onChange={e => setNewUser({ ...newUser, activate_va: e.target.checked })}
                    data-testid="checkbox-activate-va"
                    className="w-4 h-4 rounded border-gray-300 text-[#E8678B] focus:ring-[#E8678B]"
                  />
                  <span className="text-sm text-gray-700">Activate virtual account immediately</span>
                </label>
              </div>
            </div>
            <div className="flex gap-3">
              <button type="submit" disabled={creating}
                data-testid="create-wallet-btn"
                className="px-5 py-2.5 bg-[#E8678B] text-white rounded-lg text-sm font-semibold hover:bg-[#d4557a] transition-colors disabled:opacity-50">
                {creating ? 'Creating...' : 'Create Wallet'}
              </button>
              <button type="button" onClick={() => setShowAddForm(false)}
                data-testid="cancel-add-user-btn"
                className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filter Tabs + Search */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex bg-gray-100 rounded-lg p-1" data-testid="wallet-filter-tabs">
          {[
            { key: 'all', label: 'All Wallets' },
            { key: 'active', label: 'VA Active', count: stats?.wallets?.with_va },
            { key: 'needs_va', label: 'Need Activation', count: needsVA },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              data-testid={`filter-tab-${tab.key}`}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                filter === tab.key
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                  tab.key === 'needs_va' && tab.count > 0
                    ? 'bg-orange-100 text-orange-700'
                    : 'bg-gray-200 text-gray-600'
                }`}>{tab.count}</span>
              )}
            </button>
          ))}
        </div>
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text" value={search} onChange={e => setSearch(e.target.value)}
            data-testid="activation-search-input"
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8678B]/20 focus:border-[#E8678B]"
            placeholder="Search by name, user ID, or account number..."
          />
        </div>
        <button onClick={() => { fetchWallets(); fetchStats(); }}
          data-testid="refresh-btn"
          className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
          <RefreshCw size={16} className="text-gray-500" />
        </button>
      </div>

      {/* Wallet Table */}
      {loading ? (
        <div className="text-center py-12 text-gray-400" data-testid="loading-state">
          <Loader2 size={24} className="mx-auto animate-spin mb-2" />
          Loading wallets...
        </div>
      ) : wallets.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200" data-testid="empty-state">
          <Wallet size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 text-sm">
            {filter === 'needs_va' ? 'All wallets have virtual accounts!' : 'No wallets yet'}
          </p>
          {filter === 'all' && (
            <button onClick={() => setShowAddForm(true)}
              data-testid="empty-add-user-btn"
              className="mt-3 text-sm text-[#E8678B] font-medium hover:underline">
              Add your first user
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="activation-table">
          {/* Table header */}
          <div className="hidden sm:grid grid-cols-12 gap-4 px-5 py-3 bg-gray-50 border-b border-gray-100 text-xs font-medium text-gray-500 uppercase tracking-wide">
            <div className="col-span-3">User</div>
            <div className="col-span-2">User ID</div>
            <div className="col-span-2 text-right">Balance</div>
            <div className="col-span-3">Virtual Account</div>
            <div className="col-span-2 text-right">Action</div>
          </div>

          <div className="divide-y divide-gray-100">
            {wallets.map(w => {
              const hasVA = w.virtual_account?.has_va;
              const isActivating = activatingId === w.wallet_id;

              return (
                <div key={w.wallet_id}
                  data-testid={`activation-row-${w.wallet_id}`}
                  className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 px-5 py-4 hover:bg-gray-50 transition-colors items-center">

                  {/* User name + email */}
                  <div className="col-span-3 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-pink-50 flex items-center justify-center shrink-0">
                      <span className="text-[#E8678B] font-semibold text-sm">
                        {w.name?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{w.name}</p>
                      <p className="text-xs text-gray-400 truncate">{w.email || '—'}</p>
                    </div>
                  </div>

                  {/* User ID */}
                  <div className="hidden sm:block col-span-2">
                    <code className="text-xs text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded">{w.user_id}</code>
                  </div>

                  {/* Balance */}
                  <div className="hidden sm:block col-span-2 text-right">
                    <p className="text-sm font-semibold text-gray-900">₦{Number(w.balance).toLocaleString()}</p>
                  </div>

                  {/* VA Status */}
                  <div className="hidden sm:block col-span-3">
                    {hasVA ? (
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-green-500 shrink-0"></span>
                        <code className="text-sm text-gray-700">{w.virtual_account.number}</code>
                        <button onClick={() => copyVA(w.virtual_account.number)}
                          data-testid={`copy-va-${w.wallet_id}`}
                          className="text-gray-400 hover:text-gray-600">
                          <Copy size={12} />
                        </button>
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full bg-orange-100 text-orange-700">
                        <AlertCircle size={12} /> No virtual account
                      </span>
                    )}
                  </div>

                  {/* Action */}
                  <div className="hidden sm:block col-span-2 text-right">
                    {hasVA ? (
                      <span className="text-xs text-green-600 font-medium" data-testid={`va-active-${w.wallet_id}`}>Active</span>
                    ) : (
                      <button
                        onClick={() => activateVA(w.wallet_id)}
                        disabled={isActivating}
                        data-testid={`activate-va-btn-${w.wallet_id}`}
                        className="px-3 py-1.5 bg-[#E8678B] text-white rounded-lg text-xs font-semibold hover:bg-[#d4557a] transition-colors disabled:opacity-50"
                      >
                        {isActivating ? 'Activating...' : 'Activate VA'}
                      </button>
                    )}
                  </div>

                  {/* Mobile row */}
                  <div className="col-span-12 sm:hidden flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {hasVA
                        ? <span className="text-xs text-green-600 flex items-center gap-1"><CreditCard size={10} /> {w.virtual_account.number}</span>
                        : <span className="text-xs text-orange-700 bg-orange-100 px-2 py-0.5 rounded-full">No VA</span>
                      }
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="text-sm font-semibold text-gray-900">₦{Number(w.balance).toLocaleString()}</p>
                      {!hasVA && (
                        <button onClick={() => activateVA(w.wallet_id)} disabled={isActivating}
                          className="px-2 py-1 bg-[#E8678B] text-white rounded text-xs font-semibold disabled:opacity-50">
                          {isActivating ? '...' : 'Activate'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 text-xs text-gray-500">
            Showing {wallets.length} of {total} wallets
          </div>
        </div>
      )}
    </div>
  );
}
